import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const teamMembers = [
  {
    name: '林婉清',
    role: '创始人 & 主编',
    bio: '两个孩子的母亲，前儿科护士，国家二级心理咨询师。因为自己经历了新手妈妈的迷茫与焦虑，决定创办「年轻的母亲」，帮助更多妈妈在育儿路上找到方向与力量。',
    image: 'https://readdy.ai/api/search-image?query=A%20warm%20smiling%20Chinese%20woman%20in%20her%20mid%2030s%2C%20professional%20yet%20approachable%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20gentle%20expression%2C%20editorial%20portrait%20photography%20with%20soft%20shadows%2C%20warm%20neutral%20tones&width=400&height=400&seq=team-01-lin&orientation=squarish',
  },
  {
    name: '陈思雨',
    role: '内容总监',
    bio: '发展与教育心理学硕士，前亲子杂志资深编辑。专注于将专业育儿知识转化为妈妈们听得懂、用得上的实用内容。坚信每一篇好文章都能照亮一位妈妈的育儿之路。',
    image: 'https://readdy.ai/api/search-image?query=A%20kind%20Chinese%20woman%20in%20her%20early%2030s%20with%20glasses%2C%20intellectual%20and%20warm%20presence%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20warm%20neutral%20tones%2C%20professional%20and%20friendly&width=400&height=400&seq=team-02-chen&orientation=squarish',
  },
  {
    name: '张悦然',
    role: '社群运营',
    bio: '曾经的互联网产品经理，成为妈妈后转型母婴社群运营。擅长打造温暖有爱的妈妈互助社区，让每一位成员都能在这里找到归属感与支持感。',
    image: 'https://readdy.ai/api/search-image?query=A%20cheerful%20Chinese%20woman%20in%20her%20late%2020s%2C%20energetic%20and%20friendly%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography%2C%20warm%20neutral%20tones%2C%20approachable%20smile&width=400&height=400&seq=team-03-zhang&orientation=squarish',
  },
];

const milestones = [
  { year: '2023', event: '「年轻的母亲」正式上线，首发 30 篇原创育儿文章' },
  { year: '2024', event: '读者突破 10 万，开设心理调适与产后恢复专栏' },
  { year: '2025', event: '推出线上妈妈成长社群，累计服务超过 50 万年轻母亲' },
  { year: '2026', event: '持续扩展内容生态，覆盖育儿全周期的深度内容平台' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    setFormStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9spc314ne17ei0sdll0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="了解年轻的母亲品牌故事、创始团队与使命愿景。我们致力于为每一位年轻母亲提供科学育儿知识与温暖陪伴。"
        keywords="年轻的母亲,关于我们,品牌故事,创始团队,育儿平台"
        canonicalUrl="https://youngmom.readdy.ai/about"
      />
      <Navbar />

      <main className="pt-24 bg-background-50">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 mb-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Hero */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-5">
              关于我们
            </h1>
            <p className="text-foreground-700 leading-relaxed text-base md:text-lg max-w-2xl mx-auto">
              「年轻的母亲」诞生于一位新手妈妈的深夜崩溃。我们相信，每一位年轻的母亲都值得被看见、被理解、被支持。
              从第一篇育儿文章开始，我们始终秉持着一个信念：<strong>育儿不是一场孤独的修行，我们在一起。</strong>
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  用科学、温暖、实用的内容，帮助每一位年轻母亲在育儿路上少走弯路，在角色转变中找到自信与力量。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为中国年轻母亲首选的育儿成长平台，让每一位妈妈都能在这里获得知识、慰藉与归属感。构建一个温暖、互助、专业的母亲成长社区。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mx-auto mb-4">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  科学为本，温暖同行。我们坚持每一条建议都有据可依，每一次表达都充满善意。不贩卖焦虑，不制造恐慌，用真诚和专业陪伴每一位妈妈。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">
              我们的成长足迹
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-8 mb-8 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="md:w-1/2 md:text-right flex-shrink-0">
                    <div className="md:hidden w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-sm font-bold mb-2">
                      {m.year.slice(2)}
                    </div>
                    <span className="hidden md:inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-500"></span>
                    <span className="font-heading text-lg font-semibold text-foreground-900 md:hidden">
                      {m.year}
                    </span>
                  </div>
                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-100"></div>
                  <div className="md:w-1/2">
                    <span className="hidden md:block font-heading text-lg font-semibold text-foreground-900 mb-1">
                      {m.year}
                    </span>
                    <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-12">
              我们的团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden text-center"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      title={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">
                      {member.name}
                    </h3>
                    <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10 text-sm md:text-base">
              有任何建议、合作意向或想要分享你的故事？欢迎给我们留言。
            </p>

            {formStatus === 'success' ? (
              <div className="text-center py-10 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-4">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-600">感谢你的留言，我们会在 2 个工作日内回复你。</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-5 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请告诉我们你的想法或需求（限 500 字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                {formStatus === 'error' && (
                  <p className="text-sm text-rose-600">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'sending' ? '发送中...' : '发送留言'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}